import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';
import classNames from 'classnames';

import BodyCell from './bodyCells/BodyCell';
import CommentRow from './CommentRow';
import ExpanderCell from './bodyCells/ExpanderCell';

const ContactRow = ({ contact, columns, ...trProps }) => <tr key={contact.id} {...trProps}>
  {
    columns.map((column, i) => (
      <BodyCell {...column} contact={contact} key={i} />
    ))
  }
</tr>;

const ContactRowWithComments = (props) => {
  const {
    className,
    contact,
    columns
  } = props;

  const [showComments, setShowComments] = useState(false);
  const contactColumns = columns.map(col => {
    if (col.name === '_review_comments') {
      return ({
        ...col,
        onClick: () => setShowComments(!showComments)
      });
    }
    return col;
  });

  return (
    <React.Fragment>
      <ContactRow
        {...props}
        columns={contactColumns}
        className={classNames(className, showComments ? 'comments-show' : '')}
      />
      {showComments && <CommentRow comments={contact['_review_comments']} />}
    </React.Fragment>
  );
};

const useExpandableRow = (getSubRowContacts, makeSubRows) => !!getSubRowContacts || !!makeSubRows;

const ContactSection = (props) => {
  const { makeSubRows, getSubRowContacts } = props;

  if (useExpandableRow(getSubRowContacts, makeSubRows)) {
    return <ExpandableContactRow {...props} />;
  }
  return <ContactRow {...props} />;
};

const CreateContentForRow = (
  {
    columns,
    contact,
    makeSubTable,
    getSubRowContacts,
    makeSubRows,
    subTableColumnSpan
  }
) => {
  if (makeSubTable === undefined) {
    return (
      <ContactSubRows
        contact={contact}
        getSubRowContacts={getSubRowContacts}
        makeSubRows={makeSubRows}
        columns={columns}
      />
    );
  }

  const rowItems = getSubRowContacts(contact);
  const colSpanTotal = subTableColumnSpan || columns.length;

  return  (
    <tr>
      <td/>
      <td colSpan={colSpanTotal}>
        {makeSubTable(rowItems)}
      </td>
      <td/>
    </tr>
  );
};

const ExpandableContactRow = (
  {
    contact,
    makeSubRows,
    getSubRowContacts,
    columns,
    makeSubTable,
    subTableColumnSpan
  }
) => {
  const [expanded, setExpanded] = useState(false);

  const expandoColumns = columns.map(
    col => (col.type === 'expander'
      ? {
        value: (item) => {
          if (!!makeSubRows || getSubRowContacts(item).length > 0) {
            return (
              <ExpanderCell
                expanded={expanded}
                setExpanded={setExpanded}
                columnClassNames={col.columnClassNames}
              />
            );
          }
          return <td />;
        }
      } : col)
  );

  return (
    <tbody key={contact.id} className='contact-list-react-grouped-rows'>
      <ContactRow contact={contact} columns={expandoColumns} key={contact.id} />
      {expanded && (
        CreateContentForRow({
          columns, contact, makeSubTable, getSubRowContacts, makeSubRows, subTableColumnSpan
        })
      )}
    </tbody>
  );
};

const ContactSubRows = (
  {
    contact,
    getSubRowContacts,
    columns,
    makeSubRows
  }
) => {
  const subRowColumns = columns.map(col => (
    { ...col, value: (col.subRowValue || col.subRowValue === '') ? col.subRowValue : col.value }));

  if (makeSubRows) {
    return makeSubRows({ contact, columns: subRowColumns });
  }
  return getSubRowContacts(contact).map(subRowObj => <ContactRow className='contact-list-react-sub-row' contact={subRowObj} columns={subRowColumns} key={subRowObj.id} />);
};

const ContactRows = ({
  columns,
  contacts,
  emptyMessage,
  makeRow,
  getSubRowContacts,
  makeSubRows,
  makeSubTable,
  subTableColumnSpan
}) => {
  if (contacts.pending) {
    return (
      <tbody className={'contact-list-react-pending'}>
        {
          [...Array(10).keys()].map((e, i) => (
            <tr key={i}>
              <td colSpan={columns.length}>
                <ContentLoader
                  height={5}
                  width={400}
                  speed={2}
                  primaryColor='#f3f3f3'
                  secondaryColor='#ecebeb'
                >
                  <rect x='0' y='0' rx='0' ry='0' width={(Math.random() * (1 - 0.7) + 0.7) * 100} height={5} />
                </ContentLoader>
              </td>
            </tr>

          ))
        }
      </tbody>
    );
  }

  if (contacts.length === 0) {
    return (
      <tbody className={'contact-list-react-empty'}>
        <tr>
          <td colSpan={columns.length}>
            {emptyMessage}
          </td>
        </tr>
      </tbody>
    );
  }

  if (useExpandableRow(getSubRowContacts, makeSubRows)) {
    return (
      contacts.map((contact) => makeRow({ contact, columns, key: contact.id, makeSubRows, getSubRowContacts, makeSubTable, subTableColumnSpan }))
    );
  }

  return (
    <tbody className={'contact-list-react-rows'}>
      {
        contacts.map((contact) => makeRow({ contact, columns, key: contact.id }))
      }
    </tbody>
  );
};


ContactRows.propTypes = {
  /** The list of columns */
  columns: PropTypes.arrayOf(PropTypes.object),

  /** The list of contacts can be object with pending state */
  contacts: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string
    })),
    PropTypes.shape({
      pending: PropTypes.bool
    })
  ]),

  /** The message to show if there are no contacts */
  emptyMessage: PropTypes.string,

  /** Function to generate a row.  Uses a ContactRow by default */
  makeRow: PropTypes.func,

  /** Takes a "contact" and returns a list of "contacts" to apply subRowValue/value to */
  getSubRowContacts: PropTypes.func,

  /** Takes a "contact" and columns and manually creates the rows that appear when a ContactRow is expanded */
  makeSubRows: PropTypes.func,

  /** Generate sub table with the provided table items that is generated by "getSubRowContacts" function */
  makeSubTable: PropTypes.func,

  /** Optional number to indicate how many columns are spanned using `colSpan` for the nested table */
  subTableColumnSpan: PropTypes.number
};

ContactRows.defaultProps = {
  columns: [],
  contacts: [],
  emptyMessage: 'There are no contacts in this list.',
  makeRow: (props) => <ContactSection {...props} />,
  getSubRowContacts: undefined,
  makeSubRows: undefined,
  makeSubTable: undefined
};

export {
  ContactRow,
  ContactRowWithComments,
  ContactRows,
  ContactRows as default
};
